import { Entity, TransformComponent } from "excalibur";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Building } from "../actors/building";
import { TileHalfSize, TileSize } from "../constants";
import { CombinableComponent } from "../ecs/combinable";
import { TargetableComponent } from "../ecs/target";
import { TileComponent } from "../ecs/tile";
import { HudInterface } from "../hud";
import { DynamicResources, Playable } from "../types";
import { PlayableTooltip } from "./PlayableTooltip";
import { TileOverlayDisplay } from "./TileOverlayDisplay";
import { isMobile } from "../utils/mobile";

export interface TileData {
  x: number;
  y: number;
  screenX: number;
  screenY: number;
  entityId?: number; // Building entity ID for hover matching
  resources: DynamicResources;
  label: string;
}

export interface TileHoverOverlayProps {
  hudInterface: HudInterface | null;
  hoverTarget?: Entity<TargetableComponent>;
}
export const TileHoverOverlay: FC<TileHoverOverlayProps> = ({
  hudInterface,
  hoverTarget,
}) => {
  const [tileData, setTileData] = useState<TileData | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showDelayedTooltip, setShowDelayedTooltip] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mobile = isMobile();

  // Handle mouse movement for tooltip positioning
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!mobile) {
      setMousePosition({ x: e.clientX, y: e.clientY });
    }
  }, [mobile]);

  // Setup and cleanup mouse listener (not needed on mobile)
  useEffect(() => {
    if (!mobile) {
      document.addEventListener("mousemove", handleMouseMove);
      return () => document.removeEventListener("mousemove", handleMouseMove);
    }
    return () => {}; // Return empty cleanup function for mobile
  }, [handleMouseMove, mobile]);

  useEffect(() => {
    if (!hoverTarget || !hudInterface) {
      setTileData(null);
      return;
    }

    // Get tile position from hoverTarget
    const tileComponent = hoverTarget.get(TileComponent);
    if (!tileComponent?.tilemap) {
      setTileData(null);
      return;
    }

    const pos = tileComponent.pos;
    const resourceData = hudInterface.getResourcesAtTile(pos.x, pos.y);

    if (!resourceData) {
      setTileData(null);
      return;
    }

    // Get world position from transform
    const transform = hoverTarget.get(TransformComponent);
    const screenX = transform
      ? transform.pos.x - TileHalfSize
      : pos.x * TileSize;
    const screenY = transform
      ? transform.pos.y - TileHalfSize
      : pos.y * TileSize;

    setTileData({
      x: pos.x,
      y: pos.y,
      screenX,
      screenY,
      entityId: hoverTarget.id,
      resources: resourceData.total,
      label: resourceData.label,
    });

    // On mobile, set tooltip position to center of screen
    if (mobile) {
      setMousePosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      });
    }
  }, [hoverTarget, hudInterface]);

  // Get building playable from the CombinableComponent
  const getBuildingPlayable = (): Playable | null => {
    if (!hoverTarget) {
      return null;
    }

    // Only return playable if we also have tile data (confirms we're hovering over a valid tile)
    if (!tileData) {
      return null;
    }

    // Get the CombinableComponent which contains the playable
    const combinable = hoverTarget.get(CombinableComponent);
    if (combinable?.playable) {
      return combinable.playable;
    }

    // Fallback: try direct property access if it's a Building instance
    if (hoverTarget instanceof Building) {
      return hoverTarget.playable;
    }

    return null;
  };

  // Timer management for delayed tooltip
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Get building playable to check if we should show tooltip
    const buildingPlayable = getBuildingPlayable();

    // If we have a building, start timer (immediate on mobile, 300ms on desktop)
    if (buildingPlayable) {
      const delay = mobile ? 0 : 300;
      timeoutRef.current = setTimeout(() => {
        setShowDelayedTooltip(true);
      }, delay);
    } else {
      // No building = immediately hide tooltip
      setShowDelayedTooltip(false);
    }
  }, [hoverTarget, tileData]); // Include tileData since getBuildingPlayable depends on it

  const buildingPlayable = getBuildingPlayable();

  return (
    <>
      {/* Tile overlay - only shown when we have tile data */}
      {tileData && (
        <div className="relative pointer-events-none">
          <TileOverlayDisplay
            x={tileData.x}
            y={tileData.y}
            screenX={tileData.screenX * 2}
            screenY={tileData.screenY * 2 - 40}
            resources={tileData.resources}
            label={tileData.label}
            width="64px"
            height="104px"
          />
        </div>
      )}

      {/* Tooltip for buildings - shown after 2-second delay */}
      {showDelayedTooltip && buildingPlayable && (
        <PlayableTooltip
          playable={buildingPlayable}
          visible={true}
          mousePosition={mousePosition}
          source="building"
        />
      )}
    </>
  );
};
